import type { QueryKey } from "@tanstack/react-query"

import type { QueryParams } from "@/lib/api/client"

/**
 * Every query is cached under the API path it reads, and its query string
 * when it has one, so that what the API says it changed is something the
 * cache can find without knowing anything about the domain.
 */
export function apiKey(path: string, params?: QueryParams | object) {
  return params === undefined ? ([path] as const) : ([path, params] as const)
}

/**
 * Whether a cached query reads something the API changed. A changed path
 * names an endpoint whatever its query string; one ending in `/*` names that
 * endpoint and everything beneath it, so `/*` is everything.
 */
export function readsChanged(queryKey: QueryKey, changed: string[]): boolean {
  const [path] = queryKey
  if (typeof path !== "string") {
    return false
  }

  return changed.some((entry) => {
    if (!entry.endsWith("/*")) {
      return path === entry
    }

    const base = entry.slice(0, -2)
    return path === base || path.startsWith(`${base}/`)
  })
}

type ChangeHandler = (paths: string[]) => Promise<void>

let onChanged: ChangeHandler = async () => {}

/** The application decides what the cache does with what changed. */
export function setChangeHandler(handler: ChangeHandler): void {
  onChanged = handler
}

/** What a write's answer says it changed, in its `X-Changed` header. */
export async function reportChanged(header: string | null): Promise<void> {
  const paths = (header ?? "")
    .split(",")
    .map((path) => path.trim())
    .filter((path) => path !== "")

  if (paths.length > 0) {
    await onChanged(paths)
  }
}
